import CustomDialog from "components/Atoms/CustomDialog"
import CustomEditableText from "components/Atoms/CustomEditableText"
import CustomSwitch from "components/Atoms/CustomSwitch"
import ButtonMutation from "components/Molecules/ButtonMutation"
import FormController from "components/Molecules/FormController"
import { useDemandDetailsLazyQuery } from "generated/graphql"
import { UPDATE_BULK_DEMANDS } from "graphql/demand/demand.mutations.gql"
import useAuth, { useFormDisabled } from "hooks/useAuth"
import { get, pick } from "lodash-es"
import React, { useMemo, useState } from "react"
import AppToaster from "utils/AppToaster"
import convertTime from "utils/convertTime"
import { getId } from "utils/custom.utils"
import SummaryRow from "../single/SummaryRow"
import { renderDemandDate } from "./DemandCard"
function QuantityStatus({ booked, total }) {
  const percentage = (booked / total) * 100
  if (percentage || percentage === 0) {
    if (percentage > 0.75) {
      return (
        <span className="vt-highlight">
          <span className="vt-success">{booked}</span> / {total}
        </span>
      )
    } else if (percentage > 0.5) {
      return (
        <span className="vt-highlight">
          <span className="vt-warning">{booked}</span> / {total}
        </span>
      )
    } else {
      return (
        <span className="vt-highlight">
          <span className="vt-danger">{booked}</span> / {total}
        </span>
      )
    }
  } else {
    return null
  }
}
const DemandDetails = ({ id, isOpen, onClose }) => {
  const [demandData, setDemandData] = useState<any>({})
  const [getDetails, { data, error }] = useDemandDetailsLazyQuery({
    onCompleted({ demandDetails }) {
      setDemandData(
        pick(demandDetails, ["isLive", "name", "description", "isVerified"])
      )
    },
  })

  const me = useAuth()
  const demandWhatsappApproval =
    me?.user?.currentDepartment?.demandWhatsappApproval

  const parsedVariables = useMemo(
    () => ({
      ...demandData,
      ids: [getId(data?.demandDetails)],
    }),
    [demandData, data]
  )
  const disabled = useFormDisabled("demand", parsedVariables)
  if (error) return null
  const dd = get(data, "demandDetails", {})
  const gp = get(dd, "gradePrices", [])
  const ts = get(dd, "timeSlots", [])
  let totalBooked = 0,
    totalDemand = 0

  ts.forEach(time => {
    if (time.bookedQuantity || time.bookedQuantity === 0) {
      totalBooked += time.bookedQuantity
    }
    if (time.quantity || time.quantity === 0) {
      totalDemand += time.quantity
    }
  })

  function handleChange(path, value) {
    setDemandData(prev => ({
      ...prev,
      [path]: value,
    }))
  }

  return (
    <CustomDialog
      light
      width={"40%"}
      scrollable={false}
      icon="feed"
      title={
        <>
          <span className="vt-muted">Demand Details </span>
        </>
      }
      onOpened={e => {
        if (id)
          getDetails({
            variables: { id },
          })
      }}
      footerRightButton={
        <ButtonMutation
          disabled={disabled}
          icon="saved"
          text="Save"
          mutation={UPDATE_BULK_DEMANDS}
          variables={parsedVariables}
          onCompleted={_ => {
            AppToaster.show({
              message: "Demand updated successfully!",
              intent: "success",
              icon: "tick",
            })
            onClose()
          }}
          onError={err => {
            AppToaster.show({
              message: err.message,
              intent: "danger",
              icon: "cross",
            })
          }}
        />
      }
      isOpen={isOpen}
      onClose={onClose}
    >
      <FormController
        disabled={disabled}
        disableAll={true}
        source={demandData}
        onChange={handleChange}
      >
        <div>
          <SummaryRow label="Demand Name">
            <CustomEditableText
              path="name"
              onChange={(_, value) => {
                setDemandData(prev => ({
                  ...prev,
                  name: value,
                }))
              }}
              value={demandData.name}
            />
          </SummaryRow>

          <hr />
          <SummaryRow label="Variety Group:">
            <span className="vt-highlight">{get(dd, "varietyGroup.name")}</span>
          </SummaryRow>
          <SummaryRow label="Demand Date :">
            {renderDemandDate((dd as any)?.date, (dd as any)?.dateTill)}
          </SummaryRow>
          <SummaryRow label="Depot :">{get(dd, "depot.name")}</SummaryRow>
          <hr />
          <SummaryRow label="Harvest Grades :">
            {gp.map((gprices, index) => {
              return (
                <div
                  key={gprices.id}
                  className={index !== gp.length - 1 ? "mb-3" : null}
                >
                  {get(gprices, "grade.name")} @{" "}
                  <span className="vt-highlight">
                    &#8377;{gprices.price}{" "}
                    {gprices.priceLimit ? `- ${gprices.priceLimit}` : null}/{" "}
                    {get(gprices, "priceUnit.name")}
                  </span>
                </div>
              )
            })}
          </SummaryRow>
          <hr />
          <SummaryRow className="mb-3" label="Time Slots" />

          {ts.map(timeslot => {
            const timeSlt = get(timeslot, "timeSlot")
            const from = convertTime(timeSlt.fromTime)
            const to = convertTime(timeSlt.toTime)

            return (
              <SummaryRow
                key={timeslot.id}
                label={
                  <span>
                    {" "}
                    {get(timeslot, "timeSlot.name")}{" "}
                    <span className="text-sm vt-text-dark vt-highlight">
                      ({from} - {to})
                    </span>
                  </span>
                }
              >
                <div key={timeslot.id}>
                  <span className="vt-highlight">
                    <QuantityStatus
                      booked={timeslot.bookedQuantity}
                      total={timeslot.quantity}
                    />{" "}
                    <span className="vt-muted">
                      {get(timeslot, "quantityUnit.name")}
                    </span>
                  </span>
                </div>
              </SummaryRow>
            )
          })}

          <hr />
          <SummaryRow label="Bookings Summary">
            <span className="vt-highlight">
              <QuantityStatus booked={totalBooked} total={totalDemand} /> Units
            </span>
          </SummaryRow>
          <hr />
          <SummaryRow label="Attachment">
            {data?.demandDetails?.attachments?.[0]?.url ? (
              <a
                className="underline"
                href={data?.demandDetails?.attachments?.[0]?.url}
                target="_blank"
                rel="noreferrer"
              >
                {" "}
                View Attachment
              </a>
            ) : (
              <span>No attachment uploaded!</span>
            )}
          </SummaryRow>
          <hr />
          <SummaryRow label="Remark">
            <CustomEditableText
              path="description"
              onChange={(_, value) => {
                setDemandData(prev => ({
                  ...prev,
                  description: value,
                }))
              }}
              value={demandData.description}
            />
          </SummaryRow>
          <hr />

          <div className="flex justify-center items-center">
            <CustomSwitch
              intent={demandData?.isLive ? "success" : "danger"}
              className="vt-demand-live-switch"
              label={demandData.isLive ? "Live" : "Closed"}
              large
              path="isLive"
              innerLabel={null}
              disabled={demandWhatsappApproval && !demandData?.isVerified}
            />
          </div>
        </div>
      </FormController>
    </CustomDialog>
  )
}

export default DemandDetails
